import React, { type FC } from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { NavLinkButton, Col, Panel, Row } from '@shared/components/bootstrap';
import { UserRole } from '@shared/types/user_role';

import { AddressPanel } from '@admin/components/address/panel';
import { AuthorizationHoldsPanel } from '@admin/components/billing/authorization_holds/panel';
import { DamagedItemsPanel } from '@admin/components/damaged_items/damaged_items_panel';
import { DropOffManager } from '@admin/components/drop_off/manager';
import { EstimationFlowInsightPanel } from '@admin/components/estimation/insight/estimation_flow_insight_panel';
import { Roles } from '@admin/components/helpers/roles';
import { titleize } from '@admin/components/helpers/titleize';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { InventoryGrid } from '@admin/components/inventory/grid';
import { MessagesWidget } from '@admin/components/messages/widget';
import { MovingAppointmentDetails } from '@admin/components/moving/appointment_details/appointment_details';
import { MovingClocksPanel } from '@admin/components/moving/moving_clocks/panel';
import { NotesPanel } from '@admin/components/notes/panel';
import { DocumentImagesPanel } from '@admin/components/order_events/document_images_panel';
import { OrderAssessment } from '@admin/components/orders/assessment';
import { OrderAttachments } from '@admin/components/orders/attachments';
import { OrderLaborClocksPanel } from '@admin/components/orders/labor_clocks/panel';
import { OrderMap } from '@admin/components/orders/map';
import { TransportWeightUpsertForm } from '@admin/components/orders/moving/long_distance/transport_weight_upsert_form';
import { WalkthroughForm } from '@admin/components/orders/moving/walkthrough_form';
import { OrderActions } from '@admin/components/orders/order_actions';
import { OrderPickupCompleteButton } from '@admin/components/orders/order_pickup_complete_button';
import { OrderReturnRouteButton } from '@admin/components/orders/order_return_route_button';
import { ResendTodayUrlButton } from '@admin/components/orders/resend_today_url_button';
import { OrderShipmentsPanel } from '@admin/components/orders/shipments/panel';
import { OrderSummaryFields } from '@admin/components/orders/show/summary_fields';
import { OrderSignaturesPanel } from '@admin/components/orders/signatures/panel';
import { Walkthroughs } from '@admin/components/orders/walkthrough';
import { TicketsPanel } from '@admin/components/tickets/panel';
import { CreateTicket } from '@admin/components/tickets/tech_support/create_ticket';
import { accountsURL, accountURL, dispatchURL } from '@admin/config/routes';
import { useQA } from '@admin/hooks/use_qa';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  History__Subject,
  NoteNotableEnum,
  OrderSubtypeEnum,
  OrderTypeEnum,
  Ticket__TicketableEnum,
  OrderStateEnum,
  useOrderShowQuery,
} from '@admin/schema';

export const OrderShow: FC = () => {
  const { orderID } = useParams<{ orderID: string }>();
  const { data } = useOrderShowQuery({ client, variables: { id: orderID }, skip: !orderID });
  const qaMode = useQA();

  const title = data ? `${titleize(data.order.type)}: ${data.order.account.customer.name}` : 'Order';
  useTitle(title);

  if (!data?.order) return null;
  const order = data.order;
  const account = order.account;

  const showOrderEstimates =
    order.moving ||
    order.subtype === OrderSubtypeEnum.Onboarding ||
    (order.type === OrderTypeEnum.Pickup && order.subtype === OrderSubtypeEnum.Subsequent);
  const showLocationDetails = !order.movingOperation;
  const showEstimatesBesideLocation = showOrderEstimates && showLocationDetails;

  const locationDetailsSection = showLocationDetails && (
    <>
      <DropOffManager orderID={order.id} />
      <AddressPanel title="Location Details" address={order.address} account={account} />
    </>
  );

  return (
    <>
      <div className="text-overflow pad-top pad-btm">
        <ol className="breadcrumb page-header pull-left">
          <li>
            <NavLink to={accountsURL()}>Accounts</NavLink>
          </li>
          <li>
            <NavLink to={accountURL(order.account)}>
              #{account.number} ({account.customer.name})
            </NavLink>
          </li>
          <li className="active">#{order.number}</li>
        </ol>
        <Roles show={[UserRole.Admin, UserRole.L2Agent, UserRole.Manager]}>
          <div className="pull-right">
            <CreateTicket
              ticketable={{
                id: order.id,
                type: Ticket__TicketableEnum.Order,
                displayName: account.customer.name + ' (Order #' + order.number + ')',
              }}
            />
          </div>
        </Roles>
      </div>
      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          <Row>
            <Col md={6}>
              <OrderSummaryFields orderID={order.id} />
            </Col>
            <Col md={6}>
              <OrderMap orderID={order.id} />
            </Col>
          </Row>
        </Panel.Body>
        <Panel.Footer align="right">
          {order.todayApp && (
            <>
              <div className="pull-left">
                <ResendTodayUrlButton orderId={order.id} />
              </div>
              <Roles show={[UserRole.Admin]}>
                <div className="pull-left">
                  <NavLinkButton kind="info" to={order.todayApp?.url} target="_blank">
                    Today App URL
                  </NavLinkButton>
                </div>
              </Roles>
            </>
          )}
          <div className="form-inline">
            <NavLinkButton kind="default" to={accountURL(order.account)}>
              Account
            </NavLinkButton>
            {qaMode && (
              <span>
                <OrderPickupCompleteButton order={order} />
                <OrderReturnRouteButton order={order} />
              </span>
            )}
            {order.dispatch && (
              <span>
                <NavLinkButton kind="info" to={dispatchURL(order.dispatch)}>
                  View Dispatch
                </NavLinkButton>
              </span>
            )}
            <OrderActions
              canceled={order.state === OrderStateEnum.Canceled}
              accountID={account.id}
              orderID={order.id}
            />
          </div>
        </Panel.Footer>
      </Panel>
      {showEstimatesBesideLocation ? (
        <Row>
          <Col xs={12} md={6}>
            <EstimationFlowInsightPanel orderID={order.id} moving={order.moving} compactLayout />
          </Col>
          <Col xs={12} md={6}>
            {locationDetailsSection}
          </Col>
        </Row>
      ) : (
        locationDetailsSection && (
          <Row>
            <Col xs={12}>{locationDetailsSection}</Col>
          </Row>
        )
      )}
      {order.movingOperation && (
        <div>
          <Row>
            {order.subtype !== OrderSubtypeEnum.Unpacking && (
              <Col xs={12} md={6}>
                <AddressPanel title="Pickup Details" address={order.movingOperation.originAddress} account={account} />
              </Col>
            )}
            {order.subtype !== OrderSubtypeEnum.Packing && (
              <Col xs={12} md={6}>
                <AddressPanel
                  title="Delivery Details"
                  address={order.movingOperation.destinationAddress}
                  account={account}
                />
              </Col>
            )}
          </Row>
          <Panel>
            <Panel.Header className="panel-heading">
              <Panel.Title className="panel-title">Appointment Details</Panel.Title>
            </Panel.Header>
            <Panel.Body>
              <MovingAppointmentDetails orderID={order.id} />
            </Panel.Body>
          </Panel>
        </div>
      )}
      {showOrderEstimates && !showEstimatesBesideLocation && (
        <EstimationFlowInsightPanel orderID={order.id} moving={order.moving} />
      )}
      {order.moving && <WalkthroughForm orderID={order.id} />}
      {order.moving && <TransportWeightUpsertForm orderID={order.id} />}
      {order.shipped && <OrderShipmentsPanel orderID={order.id} />}
      <OrderSignaturesPanel orderID={order.id} />
      {order.assessment && <OrderAssessment assessment={order.assessment} />}
      <OrderLaborClocksPanel orderID={order.id} />
      {order.moving && order.movingOperation && <MovingClocksPanel orderID={order.id} />}
      <OrderAttachments orderID={order.id} /> <AuthorizationHoldsPanel orderID={order.id} />
      <HistoriesPanel type={History__Subject.Order} id={order.id} timezone={order && order.region && order.region.tz} />
      <NotesPanel type={NoteNotableEnum.Order} id={order.id} />
      <TicketsPanel
        ticketable={{ id: order.id, type: Ticket__TicketableEnum.Order, associatedLeadUserID: order.leadUser?.id }}
      />
      {order.walkthroughs.length > 0 && <Walkthroughs walkthroughs={order.walkthroughs} timeZone={order.region.tz} />}
      {order && <DocumentImagesPanel orderID={order.id} />}
      {order && <DamagedItemsPanel orderID={order.id} />}
      <InventoryGrid path={`/orders/${order.id}/inventory.json`} accountID={Number(account.id)} categoryEditGating />
      <MessagesWidget recipient={account} recipientType="Account" order={order} />
    </>
  );
};
